import React, { useState, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSwitch from '../components/LanguageSwitch';
import { supabase } from '../supabaseClient';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaCamera, FaSave, FaCheck, FaUser, FaGlobe } from 'react-icons/fa';
import '../styles/MyProfile.css';

const MyProfile = () => {
    const { user, profile, loading } = useAuth();
    const { t, isChinese } = useLanguage();
    const navigate = useNavigate();
    const fileInputRef = useRef(null);

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        fullName: profile?.full_name || ''
    });
    const [avatarUrl, setAvatarUrl] = useState(profile?.avatar_url || null);
    const [avatarFile, setAvatarFile] = useState(null);
    const [avatarPreview, setAvatarPreview] = useState(null);
    const [saving, setSaving] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');

    // Initialize form when profile loads
    React.useEffect(() => {
        if (profile) {
            const nameParts = (profile.full_name || '').split(' ');
            setFormData({
                firstName: nameParts[0] || '',
                lastName: nameParts.slice(1).join(' ') || '',
                fullName: profile.full_name || ''
            });
            setAvatarUrl(profile.avatar_url || null);
        }
    }, [profile]);

    const handleAvatarChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setAvatarFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setAvatarPreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const uploadAvatar = async () => {
        if (!avatarFile || !user) return null;

        const fileExt = avatarFile.name.split('.').pop();
        const fileName = `${user.id}-${Date.now()}.${fileExt}`;
        const filePath = `${fileName}`;

        const { error: uploadError } = await supabase.storage
            .from('avatars')
            .upload(filePath, avatarFile, { upsert: true });

        if (uploadError) {
            console.error('Upload error:', uploadError);
            throw uploadError;
        }

        const { data: { publicUrl } } = supabase.storage
            .from('avatars')
            .getPublicUrl(filePath);

        return publicUrl;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError('');
        setSuccess(false);

        try {
            // Build full name
            const fullName = `${formData.firstName} ${formData.lastName}`.trim();

            // Try to upload avatar if file selected (optional - may fail if storage not configured)
            let newAvatarUrl = avatarUrl;
            if (avatarFile) {
                try {
                    newAvatarUrl = await uploadAvatar();
                } catch (uploadErr) {
                    console.warn('Avatar upload failed (storage may not be configured):', uploadErr.message);
                    // Continue without avatar - don't block the save
                }
            }

            // Try update with avatar_url first
            let updateError = null;
            try {
                const result = await supabase
                    .from('profiles')
                    .update({
                        full_name: fullName,
                        avatar_url: newAvatarUrl
                    })
                    .eq('id', user.id);
                updateError = result.error;
            } catch (err) {
                updateError = err;
            }

            // If failed (maybe avatar_url column doesn't exist), try without avatar_url
            if (updateError) {
                console.warn('Full update failed, trying without avatar_url:', updateError.message);
                const { error: simpleError } = await supabase
                    .from('profiles')
                    .update({ full_name: fullName })
                    .eq('id', user.id);

                if (simpleError) throw simpleError;
            }

            setSuccess(true);
            setAvatarFile(null);
            if (newAvatarUrl) setAvatarUrl(newAvatarUrl);

            // Reload page to refresh profile data
            setTimeout(() => {
                window.location.reload();
            }, 1500);

        } catch (err) {
            console.error('Save error:', err);
            setError(err.message || 'Erreur lors de la sauvegarde');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="profile-container">
                <div className="spinner-border text-white" role="status"></div>
            </div>
        );
    }

    const displayAvatar = avatarPreview || avatarUrl;
    const initials = formData.firstName?.[0]?.toUpperCase() || formData.lastName?.[0]?.toUpperCase() || <FaUser />;

    return (
        <div className="profile-container">
            <div>
                <a href="#" onClick={(e) => { e.preventDefault(); navigate(-1); }} className="back-link">
                    <FaArrowLeft /> {isChinese ? '返回' : 'Retour'}
                </a>

                <div className="profile-card">
                    <div className="profile-header">
                        <div className="avatar-section">
                            {displayAvatar ? (
                                <img src={displayAvatar} alt="Avatar" className="avatar-image" />
                            ) : (
                                <div className="avatar-placeholder">
                                    {initials}
                                </div>
                            )}
                            <label className="avatar-upload-overlay">
                                <FaCamera />
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    onChange={handleAvatarChange}
                                />
                            </label>
                        </div>
                        <h1>{isChinese ? '个人中心' : 'Mon Profil'}</h1>
                    </div>

                    <div className="profile-body">
                        {success && (
                            <div className="success-message">
                                <FaCheck /> {isChinese ? '个人信息更新成功！' : 'Profil mis à jour avec succès !'}
                            </div>
                        )}

                        {error && (
                            <div className="error-message">
                                {error}
                            </div>
                        )}

                        <form onSubmit={handleSubmit}>
                            <div className="form-row">
                                <div className="form-group">
                                    <label className="form-label">{isChinese ? '名字' : 'Prénom'}</label>
                                    <input
                                        type="text"
                                        className="form-input"
                                        value={formData.firstName}
                                        onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                                        placeholder={isChinese ? "您的名字" : "Votre prénom"}
                                    />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">{isChinese ? '姓氏' : 'Nom'}</label>
                                    <input
                                        type="text"
                                        className="form-input"
                                        value={formData.lastName}
                                        onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                                        placeholder={isChinese ? "您的姓氏" : "Votre nom"}
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label className="form-label">{isChinese ? '电子邮箱' : 'Email'}</label>
                                <input
                                    type="email"
                                    className="form-input"
                                    value={user?.email || ''}
                                    disabled
                                />
                            </div>

                            <div className="form-group" style={{ marginTop: '16px', padding: '14px', background: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                                <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', fontWeight: 'bold' }}>
                                    <FaGlobe className="text-primary" /> {isChinese ? '平台显示语言' : 'Langue de la plateforme'}
                                </label>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                                    <LanguageSwitch className="light-theme" />
                                    <span className="text-muted small">
                                        {isChinese ? '已自动保存语言偏好' : 'La langue est automatiquement mémorisée sur votre appareil.'}
                                    </span>
                                </div>
                            </div>

                            <button type="submit" className="save-button" disabled={saving}>
                                {saving ? (
                                    <>{isChinese ? '保存中...' : 'Sauvegarde...'}</>
                                ) : (
                                    <><FaSave /> {isChinese ? '保存设置' : 'Enregistrer'}</>
                                )}
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MyProfile;
